<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import UIKit, { type ITableSortSettings, type ITableColumn } from '@pocketprep/ui-kit'
import ContentBody from '@/components/ContentBody.vue'
import GroupQuestionRow from '@/components/GroupQuestionRow.vue'
import ContentTab from '@/components/Content/ContentTab.vue'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import { stripHtmlTags } from '@/utils'
import ExamVersionDropdown from '@/components/ExamVersionDropdown.vue'
import { analyticsModule } from '@/store/analytics/module'
import type { TGroupQuestionRow, TGroupQuestions } from '@/store/types'

const {
    Search,
    FilterDropdown,
    Table,
    Pill,
    TablePagination,
    EmptyState,
    Icon,
} = UIKit

type TSubjectOption = {
    id: string
    label: string
    enabled: boolean
}

const route = useRoute()
const router = useRouter()

// Tab control
const activeTab = ref('All')

// Search
const searchString = ref('')

// Filter options
const activeFilter = ref<string | null>(null)
const checkboxOptions = ref<TSubjectOption[]>([{
    id: 'test',
    label: 'label',
    enabled: false,
}])

// Pagination
const currentPage = ref(0)
const perPage = ref(20)

// Table
const tableColumns: ITableColumn[] = [{
    name: 'Question',
    propName: 'question',
    isSortDisabled: true,
    styles: {
        width: '536px',
    },
}, {
    name: 'Answered',
    propName: 'numStudentsAnswered',
}, {
    name: 'Correct',
    propName: 'percentStudentsCorrect',
}, {
    name: 'Flagged',
    propName: 'numStudentsFlagged',
}]

const sortSettings = ref<ITableSortSettings>({ column: tableColumns[1], direction: 1 })

const isLoading = ref(false)

const groupStats = computed(() => {
    return statsModule.getters.getCurrentGroupAggStats()
})

const groupAnsweredQuestions = computed(() => {
    return groupStats.value.questionStats.filter(q => q.answers.length)
})

const groupFlaggedQuestions = computed(() => {
    return groupStats.value.questionStats.filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0)
})

const currentGroup = computed(() => {
    return groupModule.getters.getCurrentGroup()
})

const questions = computed(() => {
    // regex escape from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
    const promptRegex = new RegExp(searchString.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    return examModule.getters.getCurrentExamQuestionsNoMock().reduce<TGroupQuestions>((acc, question) => {
        const serial = question.serial
        const numStudentsFlagged = getFlaggedCountForQuestion(serial)
        const groupAnsweredQuestion = getGroupAnsweredQuestion(serial)

        // The number of students that have answered it
        const numStudentsAnswered = groupAnsweredQuestion?.totalAnswers || 0

        // The number of students that got it correct
        const numStudentsCorrect = groupAnsweredQuestion?.correctAnswerCount || 0

        // Percentage correct defaults to --
        let percentStudentsCorrect: number | string = '--'

        if (numStudentsAnswered) {
            percentStudentsCorrect = Math.round((numStudentsCorrect / numStudentsAnswered) * 100) || 0
        }
        const subject = question.subject as Study.Class.SubjectJSON
        let prompt = stripHtmlTags(question.prompt)
        if (searchString.value && !prompt.toLowerCase().includes(searchString.value.toLowerCase())) {
            return acc
        } else if (searchString.value) {
            prompt = prompt.replace(promptRegex, '<span>$&</span>')
        }
        if (enabledFilterSubjects.value.length && !enabledFilterSubjects.value.includes(subject.name)) {
            return acc
        }
        const scenarioPartLabel = examModule.getters.getScenarioPartLabel(question)
        const questionRow = {
            id: Math.random(),
            serial,
            prompt,
            numStudentsFlagged,
            numStudentsAnswered,
            percentStudentsCorrect,
            subject: subject.name,
            tabIndex: 0,
            scenarioPartLabel,
        }
        if (numStudentsFlagged > 0) {
            acc.Flagged?.push(questionRow)
        }
        acc.All?.push(questionRow)
        return acc
    }, { All: [], Flagged: [] })
})

const currentTabQuestions = computed(() => {
    return questions.value[activeTab.value]
})

const pagedSortedQuestionsActiveTab = computed(() => {
    const perPageVal = perPage.value
    const currentTabQuestionsVal = currentTabQuestions.value
    const sortSettingsVal = sortSettings.value
    return currentTabQuestionsVal?.sort((a, b) => {
        if (sortSettingsVal?.column) {
            if (sortSettingsVal.column.name === 'Answered') {
                return sortSettingsVal.direction * (b.numStudentsAnswered - a.numStudentsAnswered)
            } else if (sortSettingsVal.column.name === 'Flagged') {
                return sortSettingsVal.direction * (b.numStudentsFlagged - a.numStudentsFlagged)
            } else if (sortSettingsVal.column.name === 'Correct') {
                // Possible for the percent value to be a string of '--', so treat that
                // as less than 0 for sorting purposes
                const updatedSortValueB = b.percentStudentsCorrect === '--'
                    ? -1
                    : b.percentStudentsCorrect as number
                const updatedSortValueA = a.percentStudentsCorrect === '--'
                    ? -1
                    : a.percentStudentsCorrect as number
                return sortSettingsVal.direction * (updatedSortValueB - updatedSortValueA)
            }
        }
        return -1
    })
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const enabledFilterSubjects = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            let label = s.label
            if (label.includes('(')) {
                /*
                    Have to trim the count out the label before adding it to the filter subjects structure
                    (if it has a count)
                */
                label = label.split('(')[0]?.slice(0, -1) || ''
            }
            acc.push(label)
        }
        return acc
    }, [])
})

const formattedGroupName = computed(() => {
    if (!currentGroup.value) {
        return {
            firstLine: '',
            secondLine: '',
        }
    }

    const groupNameWords: string[] = currentGroup.value.name.split(' ')
    return groupNameWords.reduce<{
        firstLine: string
        secondLine: string
    }>((acc) => {
        const newFirstLine = `${acc.firstLine}${acc.firstLine ? ' ' : ''}${acc.secondLine.split(' ')[0]}`
        const newSecondLine = acc.secondLine.split(' ').slice(1).join(' ')
        const originalLengthDiff = Math.abs(acc.secondLine.length - acc.firstLine.length)
        const newLengthDiff = Math.abs(newSecondLine.length - newFirstLine.length)
        if (newLengthDiff < originalLengthDiff) {
            acc.firstLine = newFirstLine
            acc.secondLine = newSecondLine
        }

        return acc
    }, {
        firstLine: '',
        secondLine: groupNameWords.join(' '),
    })
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const emptyStateTitle = computed(() => {
    return `No results for "${searchString.value}"`
})

const clickChangeTab = async (tab: 'All' | 'Flagged') => {
    // Swap to clicked tab
    activeTab.value = tab

    // Reset pagination to 0
    currentPage.value = 0

    // Reload filter options
    setFilterOptions()

    if (tab === 'Flagged') {
        await userModule.actions.trackEvent('Group_Flagged_Questions')
    }

    analyticsModule.actions.amplitudeTrack('Tab Clicked', {
        tabContext: 'Group Content',
        tabName: tab,
    })
}

const changePage = (newPage: number) => {
    currentPage.value = newPage

    // Scroll to top
    scrollTop()
}

const scrollTop = () => {
    const contentEl = document.querySelector('.content-body-exam')
    contentEl?.scrollIntoView()
}

const isFilterEnabled = (subject: string) => {
    const checkboxIndex = checkboxOptions.value.findIndex(s => s.id === subject && s.enabled === true)
    if (checkboxIndex > -1) {
        return true
    }
    return false
}

const setFilterOptions = (enabledFilterOption?: string | null) => {
    const examMetadataVal = currentExamMetadata.value
    checkboxOptions.value = Object.values(examMetadataVal?.knowledgeAreas || {}).map(subject => ({
        id: subject.name,
        label: `${subject.name} (${getQuestionCountForSubject(subject.name)})`,
        enabled: isFilterEnabled(subject.name) || enabledFilterOption === subject.name,
    })).sort((a, b) => a.id > b.id ? 1 : -1) // Sort alphabetically / numerically
}

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o => o.id === filterId ? { ...o, enabled: false } : o)
}

const getQuestionCountForSubject = (subjectName: string) => {
    const promptRegex = new RegExp(searchString.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')

    const qs = examModule.getters.getCurrentExamQuestionsNoMock().reduce<TGroupQuestions>((acc, question) => {
        const serial = question.serial
        const numStudentsFlagged = getFlaggedCountForQuestion(serial)
        const subject = question.subject as Study.Class.SubjectJSON
        let prompt = question.prompt.replace(/<[^\s>]+[^>]*>/gi, ' ')
        if (searchString.value && !prompt.toLowerCase().includes(searchString.value.toLowerCase())) {
            return acc
        } else if (searchString.value) {
            prompt = prompt.replace(promptRegex, '<span>$&</span>')
        }

        const questionRow = {
            id: Math.random(),
            serial,
            prompt,
            numStudentsFlagged,
            numStudentsAnswered: 10,
            percentStudentsCorrect: 10,
            subject: subject.name,
            tabIndex: 0,
        }
        if (numStudentsFlagged > 0) {
            acc.Flagged?.push(questionRow)
        }
        acc.All?.push(questionRow)
        return acc
    }, { All: [], Flagged: [] })
    return qs[activeTab.value]?.filter((s: TGroupQuestionRow) => s.subject === subjectName).length
}

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    sortSettings.value = sortSettingsVal

    analyticsModule.actions.amplitudeTrack('Table Sort Clicked', {
        tableContext: 'Group Content',
        tableSortColumn: sortSettingsVal.column?.propName,
        tableSortDirection: sortSettingsVal.direction,
    })
}

const getFlaggedCountForQuestion = (serial: string) => {
    const groupFlaggedQuestion = groupFlaggedQuestions.value.find(flaggedQ =>
        flaggedQ.serial === serial)
    return groupFlaggedQuestion?.numStudentsFlagged || 0
}

const getGroupAnsweredQuestion = (serial: string) => {
    const groupAnsweredQuestion = groupAnsweredQuestions.value.find(answeredQ =>
        answeredQ.serial === serial)
    return groupAnsweredQuestion
}

const rowClicked = (row: { serial: string }) => {
    if (activeTab.value === 'Flagged') {
        const clickedQuestion = questions.value.Flagged?.find(q => {
            return q.serial === row.serial
        })

        if (clickedQuestion?.serial) {
            if (enabledFilterSubjects.value?.length) {
                router.push({
                    name: route.name as string,
                    params: { ...route.params },
                    query: {
                        questionSerial: clickedQuestion?.serial,
                        type: 'flagged',
                        subjects: enabledFilterSubjects.value,
                    },
                })
            } else {
                router.push({
                    name: route.name as string,
                    params: { ...route.params },
                    query: {
                        questionSerial: clickedQuestion?.serial,
                        type: 'flagged',
                    },
                })
            }
        }
    }

    if (activeTab.value === 'All') {
        const clickedQuestion = questions.value.All?.find(q => {
            return q.serial === row.serial
        })

        if (enabledFilterSubjects.value?.length) {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'all',
                    subjects: enabledFilterSubjects.value,
                },
            })
        } else {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'all',
                },
            })
        }
    }
}

onBeforeRouteUpdate((to, from, next) => {
    // Ensure we are up to date on group, student, exam etc
    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    studentModule.actions.updateCurrentStudentId(null)
    instructorModule.actions.fetchCurrentOrgInstructor()
    groupModule.actions.fetchCurrentVisibleOrgGroups()
    groupModule.actions.fetchCurrentGroup()
    examModule.actions.fetchCurrentGroupExam()
    statsModule.actions.fetchCurrentGroupAggStats()
    examModule.actions.fetchExamMetadata()
    examModule.actions.fetchCurrentGroupExam()

    // Adjust filter options based on the current exam
    setFilterOptions()
    next()
})

onMounted(async () => {
    isLoading.value = true
    const routeGroupId = route.params.groupId

    // Checking the tab qs param
    if (route.query?.tab === 'flagged') {
        activeTab.value = 'Flagged'
        await userModule.actions.trackEvent('Group_Flagged_Questions')
    } else if (route.query?.tab === 'all') {
        activeTab.value = 'All'
    }

    // Check if there is an incoming subject filter from Stats
    if (route.query?.subject && typeof route.query?.subject === 'string') {
        activeFilter.value = route.query?.subject
    }

    // Load group / student data
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructor(),
        groupModule.actions.fetchCurrentVisibleOrgGroups(),
        groupModule.actions.fetchCurrentGroup(),
        examModule.actions.fetchExamMetadata(),
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        statsModule.actions.fetchCurrentGroupAggStats(),
        studentModule.actions.updateCurrentStudentId(null),
    ])

    // Set options for filtering
    setFilterOptions(activeFilter.value)

    // Give time for filters to update
    nextTick(() => {
        isLoading.value = false
    })
})

watch([ () => questions.value.All?.length, () => questions.value.Flagged?.length ], () => {
    setFilterOptions()
})

watch(enabledFilterSubjects, (newVal: string[], oldVal: string[]) => {
    const numNewEnabledFilters = newVal.length
    const filtersDidChange = newVal.length !== oldVal.length
    if (filtersDidChange && !isLoading.value) {
        analyticsModule.actions.amplitudeTrack('Table Filtered', {
            tableName: 'Group Content',
            didFilterSubject: true,
            subjectFiltersCount: numNewEnabledFilters,
        })
    }
}, { deep: true })
</script>

<template>
    <ContentBody
        class="group-content"
    >
        <template #contentBodyExam>
            <div class="group-content__group-name-container">
                <ExamVersionDropdown class="group-content__exam" />
            </div>
        </template>
        <template #contentBodyTitle>
            <div class="group-content-head__group-name">
                <div>
                    {{ formattedGroupName.firstLine }}<br>
                    {{ formattedGroupName.secondLine }}
                </div>
            </div>
        </template>
        <template #contentTabs>
            <div
                class="group-content__tabs"
                role="tablist"
            >
                <ContentTab
                    key="All"
                    :is-active="activeTab === 'All'"
                    label="All"
                    :count="questions['All']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'All' ? 'true' : 'false'"
                    @keydown.enter="activeTab = 'All'"
                    @click="clickChangeTab('All')"
                />
                <ContentTab
                    key="Flagged"
                    :is-active="activeTab === 'Flagged'"
                    label="Flagged"
                    :count="questions['Flagged']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'Flagged' ? 'true' : 'false'"
                    @keydown.enter="activeTab = 'Flagged'"
                    @click="clickChangeTab('Flagged')"
                />
            </div>
        </template>
        <template #contentSearch>
            <div v-if="!isLoading">
                <div class="group-content__search">
                    <div class="group-content__search-container">
                        <Search
                            class="group-content__search-questions"
                            v-model="searchString"
                            placeholder="Search Questions"
                            type="white"
                            @input="setFilterOptions"
                        />
                    </div>
                    <FilterDropdown
                        v-model="checkboxOptions"
                        size="large"
                        class="group-content__filter"
                        filterLabel="Filter by Subject"
                        :checkboxEntries="checkboxOptions"
                        :radioEntries="[]"
                    />
                </div>
                <div v-if="enabledFilterSubjects.length" class="group-content__filters">
                    <div class="group-content__filters-label">
                        Filters:
                    </div>
                    <Pill
                        v-for="filter in enabledFilterSubjects"
                        :key="filter"
                        class="group-content__filters-pill"
                        :show-close="true"
                        @click="removeFilter(filter)"
                    >
                        {{ filter }}
                    </Pill>
                </div>
            </div>
        </template>
        <template #contentQuestions>
            <div v-if="!isLoading">
                <div
                    v-if="questions[activeTab]?.length === 0 && searchString !== ''"
                    class="group-content__no-activity"
                >
                    <EmptyState
                        class="group-content__empty-state"
                        :title="emptyStateTitle"
                        message="Only question text is searchable. Check your spelling or try again."
                    >
                        <template #imageRow>
                            <div />
                        </template>
                    </EmptyState>
                </div>
                <div
                    v-if="questions['Flagged']?.length === 0 && activeTab === 'Flagged' && searchString === ''"
                    class="group-content__no-activity"
                >
                    <EmptyState
                        class="group-content__empty-state"
                        state="flagged"
                        title="0 Flagged Questions"
                        message="Once students flag questions you will be able to see them here."
                    />
                </div>
                <Table
                    v-if="currentTabQuestions?.length"
                    class="group-content__table-all"
                    theme="open"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :rows="pagedSortedQuestionsActiveTab || []"
                    :columns="tableColumns"
                    :show-header="false"
                    :table-content-styles="{
                        boxSizing: 'none',
                    }"
                    :table-column-label-styles="{
                        margin: '0 auto',
                        display: 'flex',
                        fontSize: '12px',
                        color: '#587183',
                    }"
                    :table-cell-styles="{
                        display: 'flex',
                        position: 'relative',
                    }"
                    :table-sort-icon-styles="{
                        position: 'absolute', top: '50%', right: '-15px', marginTop: '-4.5px'
                    }"
                    @rowClicked="rowClicked({serial: $event.serial as string})"
                    @sort="updateSortSettings"
                >
                    <template #tableCellValue="{ row, column }">
                        <GroupQuestionRow
                            class="group-content__question-row"
                            :row="row"
                            :column="column"
                            :num-of-questions="currentTabQuestions.length"
                        />
                    </template>
                </Table>
            </div>
            <Icon
                v-else
                type="loading2"
                class="group-content__loading"
            />
        </template>
        <template #contentPagination>
            <TablePagination
                v-if="!isLoading && currentTabQuestions && currentTabQuestions.length > 0"
                class="group-content__pagination"
                unit="Questions"
                :per-page="perPage"
                :current-page="currentPage"
                :total="currentTabQuestions.length"
                @changePage="changePage"
            />
        </template>
    </ContentBody>
</template>

<style lang="scss" scoped>
.group-content {
    :deep(*) {
        .uikit-table__cell:nth-child(2),
        .uikit-table__cell:nth-child(3),
        .uikit-table__cell:nth-child(4) {
            justify-content: right;
            width: 78px;
        }
        .uikit-table__rows:nth-child(2) {
            min-height: 375px;
            max-height: 66.5vh; // only make it so that the questions are scrollable
        }
        .uikit-table__cell {
            margin: 0 auto;
            width: 78px;
        }

        .uikit-table__cell:nth-child(2) {
            padding-right: 6px;
        }

        .uikit-table__row {
            padding-right: 0px;
        }

        .uikit-table__column-labels {
            padding-right: 0px;
        }
    }
    &__group-name {
        max-width: 320px;
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        overflow-wrap: break-word;
    }

    &__tabs {
        display: flex;
        padding-top: 65px;
    }

    &__filter {
        position: relative;
        width: 230px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;
    }

    &__no-activity {
        text-align: center;
        padding: 0 42px 0 41px;
        margin-top: 54px;
    }

    &__empty-state {
        margin: 0 auto;
    }

    &__search {
        display: flex;
        margin: 26px auto 0;
        margin-bottom: 16px;
        align-items: center;
    }

    &__search-container {
        width: 359px;
        margin-right: 20px;

        @include breakpoint(black-bear) {
            margin-right: 14px;
        }
    }

    &__group-name-container {
        display: flex;
        align-items: center;
        position: relative;
    }

    &__question-row {
        float: left;
    }

    &__pagination {
        margin: 17px 0;
        float: right;
    }

    &__loading {
        margin: 80px auto;
    }
}
</style>
